import React, { useState, useEffect } from 'react';
import { Eye, Award, Trophy, Clock, Star } from 'lucide-react';
import certificateAPI from '../services/certificateAPI';
import { toast } from 'react-hot-toast';

const Certificates = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadCertificates();
    loadStats();
  }, []);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading certificates...');
      
      const response = await certificateAPI.getAllCertificates();
      console.log('📊 Certificate API response:', response);
      
      if (response.success) {
        console.log(`✅ Loaded ${response.data.length} certificates:`, response.data);
        setCertificates(response.data);
      } else {
        console.error('❌ API returned error:', response.error);
        throw new Error(response.error || 'Failed to load certificates');
      }
    } catch (error) {
      console.error('❌ Error loading certificates:', error);
      toast.error(`Failed to load certificates: ${error.message}`);
      // Fallback to empty array if API fails
      setCertificates([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await certificateAPI.getCertificateStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      // Don't show error toast for stats, it's not critical
    }
  };

  const handleViewCertificate = async (certificate) => {
    try {
      console.log('🔍 Viewing certificate:', certificate);
      
      // Use the certificate token from the certificate object
      const certificateToken = certificate.certificate_token || certificate.certificateToken;
      
      if (!certificateToken) {
        toast.error('Certificate token not found');
        return;
      }
      
      // Open certificate in new tab
      const certificateUrl = `${import.meta.env.VITE_API_URL || process.env.RAILWAY_PUBLIC_DOMAIN || 'http://localhost:3001'}/api/certificates/${certificateToken}`;
      console.log('🔗 Opening certificate URL:', certificateUrl);
      window.open(certificateUrl, '_blank');
    } catch (error) {
      console.error('Error viewing certificate:', error);
      toast.error('Failed to load certificate');
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) {
      return 'No date available';
    }
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="header-gradient rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">🏆 Certificates</h1>
            <p className="mt-2 text-lg">
              Manage and view completion certificates
            </p>
          </div>
          <button
            onClick={() => {
              loadCertificates();
              loadStats();
            }}
            className="btn-secondary-gradient flex items-center space-x-2"
          >
            <Star className="h-5 w-5" />
            <span>Refresh</span>
          </button>
        </div>
        {stats && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/20 rounded-lg p-3">
              <div className="text-2xl font-bold">{stats.certificates?.total_certificates || 0}</div>
              <div className="text-sm opacity-90">Total Certificates</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <div className="text-2xl font-bold">{stats.certificates?.emails_sent || 0}</div>
              <div className="text-sm opacity-90">Emails Sent</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <div className="text-2xl font-bold">{stats.certificates?.certificates_last_7_days || 0}</div>
              <div className="text-sm opacity-90">Last 7 Days</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <div className="text-2xl font-bold">{stats.prizes?.total_prizes_awarded || 0}</div>
              <div className="text-sm opacity-90">Prizes Awarded</div>
            </div>
          </div>
        )}
      </div>

      {/* Certificates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {certificates.map((certificate) => (
          <div
            key={certificate.id}
            className="email-card"
          >
            <div className="email-card-header">
              <div className="flex items-center justify-center">
                <Trophy className="h-8 w-8 mr-3" />
                <h3>The Circle</h3>
              </div>
            </div>
            <div className="email-card-body">
              <div className="text-center mb-4">
                <span className="status-badge-gradient status-completed">Completed</span>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <span className="font-medium">User:</span>
                  <span className="ml-2">{certificate.userName || certificate.user_name || 'Unknown User'}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="font-medium">City:</span>
                  <span className="ml-2">{certificate.city || 'London'}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>{formatDate(certificate.completedAt || certificate.completed_at)}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="font-medium">Token:</span>
                  <span className="ml-2 font-mono text-xs">
                    {certificate.certificate_token ? 
                      `${certificate.certificate_token.substring(0, 8)}...${certificate.certificate_token.substring(certificate.certificate_token.length - 8)}` : 
                      'No token'
                    }
                  </span>
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={() => handleViewCertificate(certificate)}
                  className="w-full btn-gradient flex items-center justify-center py-3 px-6 text-lg font-semibold hover:shadow-lg transition-all duration-200"
                >
                  <Eye className="h-5 w-5 mr-3" />
                  View Certificate
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Certificate Modal */}
      {showModal && selectedCertificate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Certificate Preview
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                <div className="text-center">
                  <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Certificate of Completion
                  </h2>
                  <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                    This certifies that
                  </p>
                  <h3 className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                    {selectedCertificate.userName}
                  </h3>
                  <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                    has successfully completed
                  </p>
                  <h4 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                    The Circle
                  </h4>
                  <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                    in {selectedCertificate.city}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">
                    Completed on {formatDate(selectedCertificate.completedAt)}
                  </p>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Certificates;
